<template>
  <div class="password-strength-meter" v-if="password">
    <!-- Barra de progreso -->
    <div class="progress-container">
      <div class="progress-bar">
        <div
          class="progress-fill"
          :class="strengthColor"
          :style="{ width: `${strength}%` }"
        ></div>
      </div>
      <p class="strength-text">
        {{ getTextByLang('strengthText') }}: {{ strengthTextValue }}
      </p>
    </div>

    <!-- Advertencia -->
    <p v-if="translatedWarning" class="warning">{{ translatedWarning }}</p>

    <!-- Lista de requisitos -->
    <div class="requirements-list">
      <div
        v-for="(requirement, key) in allRequirements"
        :key="key"
        class="requirement-item"
      >
        <i v-if="requirement" class="material-icons text-success"
          >check_circle</i
        >
        <i v-else class="material-icons text-danger">cancel</i>
        <span>{{ getRequirementText(key) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import zxcvbn from 'zxcvbn'
import translations from './translations'

export default {
  name: 'PasswordStrengthMeter',
  props: {
    password: {
      type: String,
      default: '',
    },
    confirmPassword: {
      type: String,
      default: '',
    },
    // Opcionalmente puedes mantener language como prop para sobreescribir
    // el idioma de i18n si es necesario
    language: {
      type: String,
      default: 'es',
    },
  },
  data() {
    return {
      strength: 0,
      requirements: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        notCommon: false,
      },
      suggestions: [],
      warning: '',
      translatedWarning: '',
      crackedTime: '',
      translations: translations,
      currentLanguage: null, // Lo inicializaremos en mounted
    }
  },
  computed: {
    activeLanguage() {
      if (this.language && this.translations[this.language]) {
        return this.language
      }

      if (this.currentLanguage && this.translations[this.currentLanguage]) {
        return this.currentLanguage
      }

      return 'es'
    },
    strengthColor() {
      if (this.strength <= 20) return 'very-weak'
      if (this.strength <= 40) return 'weak'
      if (this.strength <= 60) return 'medium'
      if (this.strength <= 80) return 'strong'
      return 'very-strong'
    },
    strengthTextValue() {
      const levels = this.translations[this.activeLanguage].strengthLevels
      if (this.strength <= 20) return levels.veryWeak
      if (this.strength <= 40) return levels.weak
      if (this.strength <= 60) return levels.medium
      if (this.strength <= 80) return levels.strong
      return levels.veryStrong
    },
    allRequirements() {
      if (this.confirmPassword !== undefined) {
        return {
          ...this.requirements,
          match: this.password === this.confirmPassword && this.password !== '',
        }
      }
      return this.requirements
    },
    isValid() {
      // Verifica si todos los requisitos son verdaderos
      return Object.values(this.allRequirements).every(Boolean)
    },
  },
  created() {
    // Inicializar el idioma desde i18n
    if (this.$i18n && this.$i18n.locale) {
      this.currentLanguage = this.$i18n.locale
    }
  },
  methods: {
    getTextByLang(key) {
      return this.translations[this.activeLanguage][key]
    },
    checkRequirements(pass) {
      const safePass = String(pass || '')

      // Análisis básico de requisitos
      const hasLength = safePass.length >= 8
      const hasUppercase = /[A-Z]/.test(safePass)
      const hasLowercase = /[a-z]/.test(safePass)
      const hasNumber = /[0-9]/.test(safePass)
      const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(safePass)

      // Análisis avanzado con zxcvbn
      const result = zxcvbn(safePass)

      // Extraer feedback y score
      this.suggestions = result.feedback.suggestions
      this.warning = result.feedback.warning
      this.translatedWarning = this.translateWarning(result.feedback.warning)
      this.crackedTime =
        result.crack_times_display.offline_slow_hashing_1e4_per_second

      // El score va de 0 (muy débil) a 4 (muy fuerte)
      const zxcvbnScore = result.score

      // Actualizar requisitos
      this.requirements = {
        length: hasLength,
        uppercase: hasUppercase,
        lowercase: hasLowercase,
        number: hasNumber,
        special: hasSpecial,
        notCommon: zxcvbnScore > 1, // Considera como "común" si el score es 0 o 1
      }

      // Calcular fuerza combinando zxcvbn y requisitos básicos
      const basicRequirementsWeight = 30
      const zxcvbnWeight = 70

      const basicPercentage =
        (Object.values(this.requirements).filter(Boolean).length /
          Object.values(this.requirements).length) *
        100

      const zxcvbnPercentage = (zxcvbnScore / 4) * 100

      this.strength = Math.min(
        100,
        (basicPercentage * basicRequirementsWeight) / 100 +
          (zxcvbnPercentage * zxcvbnWeight) / 100
      )

      this.emitValidationState()
    },
    getRequirementText(key) {
      return this.translations[this.activeLanguage].requirements[key] || key
    },
    translateWarning(warning) {
      if (!warning) return ''

      // Si el idioma es inglés, devolvemos el warning original
      if (this.activeLanguage === 'en') return warning

      // Para otros idiomas, buscamos en el diccionario de traducciones
      const warnings = this.translations[this.activeLanguage].warnings

      // Buscar una traducción exacta
      if (warnings[warning]) {
        return warnings[warning]
      }

      // Buscar traducciones parciales (para warnings que puedan contener texto variable)
      for (const [eng, translated] of Object.entries(warnings)) {
        if (warning.includes(eng)) {
          return warning.replace(eng, translated)
        }
      }

      // Si no hay traducción, devolver el warning original
      return warning
    },
    emitValidationState() {
      // Emite el estado actual de validación y los requisitos
      this.$emit('validation-change', {
        isValid: this.isValid,
        requirements: this.allRequirements,
        strength: this.strength,
        suggestions: this.suggestions,
        warning: this.warning,
        translatedWarning: this.translatedWarning,
        crackedTime: this.crackedTime,
      })
    },
    updateTranslationsFromLocale() {
      // Si hay advertencia, actualizarla con el nuevo idioma
      if (this.warning) {
        this.translatedWarning = this.translateWarning(this.warning)
      }
    },
  },
  watch: {
    password: {
      handler(newValue) {
        this.checkRequirements(newValue)
      },
      immediate: true,
    },
    confirmPassword: {
      handler() {
        this.emitValidationState()
      },
    },
    // Observar cambios en la prop de language
    language: {
      handler() {
        this.updateTranslationsFromLocale()
      },
    },
    // Observar cambios en el idioma de i18n
    '$i18n.locale': {
      handler(newLocale) {
        this.currentLanguage = newLocale
        this.updateTranslationsFromLocale()
      },
    },
    // Observar cambios en el idioma activo (ya sea por prop o i18n)
    activeLanguage: {
      handler() {
        this.updateTranslationsFromLocale()
      },
    },
    isValid: {
      handler(newValue) {
        if (newValue) {
          this.$emit('requirements-met')
        }
      },
    },
  },
}
</script>

<style scoped>
.password-strength-meter {
  width: 100%;
}

.progress-container {
  margin-bottom: 1rem;
}

.progress-bar {
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.very-weak {
  background-color: #dc3545;
}
.weak {
  background-color: #ffc107;
}
.medium {
  background-color: #fd7e14;
}
.strong {
  background-color: #20c997;
}
.very-strong {
  background-color: #198754;
}

.strength-text {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #6c757d;
}

.requirements-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.material-icons {
  font-size: 1rem;
}

.text-success {
  color: #198754;
}

.text-danger {
  color: #dc3545;
}

.warning {
  color: #dc3545;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}
</style>
